import { useEffect, useState } from "react";
import API from "../../../services/api";
import "./allocate.css";

const PLAN_OPTIONS = [
  { value: 1,  label: "1 Month" },
  { value: 6,  label: "6 Months" },
  { value: 12, label: "1 Year" },
];

export default function AllocateStudent() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedHostel, setSelectedHostel] = useState("");
  const [selectedRoom, setSelectedRoom] = useState("");
  const [selectedBed, setSelectedBed] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");

  // ✅ NEW: Fee plan (months)
  const [planMonths, setPlanMonths] = useState(1);

  const [loading, setLoading] = useState(false);

  // ================= LOAD INITIAL DATA =================
  useEffect(() => {
    loadHostels();
    loadClasses();
    loadStudents();
  }, []);

  const loadHostels = async () => {
    const res = await API.get("/school-admin/hostels/", {
      headers: { "X-SCHOOL-ID": school.school.id },
    });
    setHostels(res.data.hostels || []);
  };

  const loadClasses = async () => {
    const res = await API.get("/school-admin/classes/", {
      headers: { "X-SCHOOL-ID": school.school.id },
    });
    setClasses(res.data.classes || []);
  };

  const loadStudents = async () => {
    const res = await API.get("/school-admin/students/", {
      headers: { "X-SCHOOL-ID": school.school.id },
    });
    setStudents(res.data.students || []);
  };

  // ================= LOAD ROOMS =================
  const loadRooms = async (hostelId) => {
    if (!hostelId) return setRooms([]);

    const res = await API.get(
      `/school-admin/hostels/${hostelId}/rooms/`,
      { headers: { "X-SCHOOL-ID": school.school.id } }
    );
    setRooms(res.data.rooms || []);
  };

  // ================= LOAD BEDS =================
  const loadBeds = async (roomId) => {
    if (!roomId) return setBeds([]);

    const res = await API.get(
      `/school-admin/rooms/${roomId}/beds/`,
      { headers: { "X-SCHOOL-ID": school.school.id } }
    );
    setBeds(res.data.beds || []);
  };

  // ================= FILTER STUDENTS BY CLASS =================
  const filteredStudents = selectedClass
    ? students.filter(
        (s) => String(s.class_id) === String(selectedClass)
      )
    : [];

  // ================= ALLOCATE STUDENT =================
  const handleAllocate = async () => {
    if (!selectedHostel || !selectedRoom || !selectedBed || !selectedStudent) {
      alert("Please select hostel, room, bed, class, and student");
      return;
    }

    setLoading(true);
    try {
      await API.post(
        "/school-admin/hostel/allocate/",
        {
          student: selectedStudent,
          room: selectedRoom,
          bed: selectedBed,
          plan_months: planMonths, // ✅ KEY ADDITION
        },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );

      alert("Student allocated successfully 🎉");

      loadBeds(selectedRoom);
      setSelectedStudent("");
      setSelectedBed("");

    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || "Allocation failed");
    } finally {
      setLoading(false);
    }
  };

  // ================= UI =================
  return (
    <div className="allocate-page">

      {/* HERO */}
      <div className="allocate-hero">
        <span className="allocate-hero-icon">🛏️</span>
        <div>
          <h1>Allocate Student to Hostel</h1>
          <p>Select hostel, room, bed and student to assign a bed</p>
        </div>
      </div>

      <div className="allocate-form">

        {/* STEP 1 — HOSTEL / ROOM / BED */}
        <div className="allocate-section">
          <div className="allocate-section-title">
            <span className="allocate-step-badge">1</span>
            <h3>Select Hostel, Room & Bed</h3>
          </div>
          <div className="allocate-fields">
            <div className="allocate-field">
              <label>Hostel</label>
              <select
                value={selectedHostel}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedHostel(v);
                  setSelectedRoom("");
                  setSelectedBed("");
                  loadRooms(v);
                }}
              >
                <option value="">Select Hostel</option>
                {hostels.map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
            </div>

            <div className="allocate-field">
              <label>Room</label>
              <select
                value={selectedRoom}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedRoom(v);
                  setSelectedBed("");
                  loadBeds(v);
                }}
                disabled={!selectedHostel}
              >
                <option value="">Select Room</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id} disabled={r.free_beds === 0}>
                    Room {r.room_number} — Free: {r.free_beds}/{r.total_beds}
                    {r.free_beds === 0 ? " (Full)" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="allocate-field">
              <label>Bed</label>
              <select
                value={selectedBed}
                onChange={(e) => setSelectedBed(e.target.value)}
                disabled={!selectedRoom}
              >
                <option value="">Select Bed</option>
                {beds.map((b) => (
                  <option key={b.id} value={b.id} disabled={b.is_occupied}>
                    Bed {b.bed_number} {b.is_occupied ? "(Occupied)" : "(Free)"}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* STEP 2 — CLASS / STUDENT */}
        <div className="allocate-section">
          <div className="allocate-section-title">
            <span className="allocate-step-badge">2</span>
            <h3>Select Student</h3>
          </div>
          <div className="allocate-fields">
            <div className="allocate-field">
              <label>Class</label>
              <select
                value={selectedClass}
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  setSelectedStudent("");
                }}
              >
                <option value="">Select Class</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="allocate-field">
              <label>Student</label>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                disabled={!selectedClass}
              >
                <option value="">
                  {selectedClass ? "Select Student" : "Select class first"}
                </option>
                {filteredStudents.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.roll_number ? ` (#${s.roll_number})` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* STEP 3 — FEE PLAN */}
        <div className="allocate-section">
          <div className="allocate-section-title">
            <span className="allocate-step-badge">3</span>
            <h3>Fee Plan</h3>
          </div>
          <div className="plan-pills">
            {PLAN_OPTIONS.map((p) => (
              <button
                key={p.value}
                className={`plan-pill${planMonths === p.value ? " active" : ""}`}
                onClick={() => setPlanMonths(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* SUBMIT */}
        <div className="allocate-submit-wrap">
          <button
            className="allocate-submit-btn"
            onClick={handleAllocate}
            disabled={loading}
          >
            {loading ? "Allocating..." : "🛏️  Allocate Student"}
          </button>
        </div>
      </div>
    </div>
  );
}